// Dynamic favicon with an unread-dot badge.
//
// Draws the app icon onto a 32x32 canvas and — when unread > 0 — overlays
// a small red dot in the top-right corner, then swaps the <link rel="icon">
// href to the resulting data URL. Reverts to the original static favicon
// when unread drops back to 0.
//
// Using a canvas means we don't have to ship a second static file or build
// a sprite sheet; the dot is drawn per-render and is SVG-like crisp at
// typical browser tab sizes.

const ICON_SRC = '/icons/icon-72x72.png';
const LINK_ID = 'app-favicon';
const SIZE = 32;

let baseImage = null;
let baseImagePromise = null;

function loadBase() {
  if (baseImage) return Promise.resolve(baseImage);
  if (baseImagePromise) return baseImagePromise;
  baseImagePromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { baseImage = img; resolve(img); };
    img.onerror = reject;
    img.src = ICON_SRC;
  });
  return baseImagePromise;
}

function ensureLink() {
  let link = document.getElementById(LINK_ID);
  if (!link) {
    link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'icon';
    link.type = 'image/png';
    document.head.appendChild(link);
  }
  return link;
}

/**
 * Swap the favicon to reflect the unread count.
 * @param {number} unread - unread notification count
 */
export async function updateFavicon(unread) {
  try {
    const link = ensureLink();
    if (!unread || unread <= 0) {
      // Reset to the plain icon.
      link.href = ICON_SRC;
      return;
    }

    const img = await loadBase();
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.drawImage(img, 0, 0, SIZE, SIZE);

    // Red dot, top-right. A thin white border keeps the dot legible even
    // when the favicon is already a dark or red icon.
    const r = 8;
    const cx = SIZE - r - 1;
    const cy = r + 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#ef4444';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    link.href = canvas.toDataURL('image/png');
  } catch (e) {
    // Silently fall back to the plain icon — favicon is non-critical.
  }
}
